import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { X, Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '@/lib/api';

// Bulk inventory upload modal. Follows the BulkInvoiceUploadModal pattern:
// file → parse (xlsx client-side) → preview API → confirm → apply API.

const HEADER_ALIASES = {
  product_id: ['상품ID', 'product_id', 'productId', 'PRODUCT_ID'],
  variant_key: ['옵션', 'variant_key', 'option', 'OPTION'],
  sku: ['SKU', 'sku', 'Sku'],
  delta: ['조정수량', 'delta', 'adjustment', '변경수량', 'ADJ'],
  reason: ['조정사유', 'reason', '사유'],
};

const _pick = (row, keys) => {
  for (const k of keys) if (row[k] !== undefined && row[k] !== null && row[k] !== '') return row[k];
  return undefined;
};

export default function BulkInventoryUploadModal({ open, onClose, onApplied, currentFilterUrl }) {
  const [file, setFile] = useState(null);
  const [rawRows, setRawRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setFile(null); setRawRows([]); setPreview(null); } }, [open]);
  if (!open) return null;

  const parseFile = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const rows = json.map((r) => ({
        product_id: _pick(r, HEADER_ALIASES.product_id),
        variant_key: _pick(r, HEADER_ALIASES.variant_key) || 'default',
        sku: _pick(r, HEADER_ALIASES.sku),
        delta: _pick(r, HEADER_ALIASES.delta),
        reason: _pick(r, HEADER_ALIASES.reason),
      }));
      setRawRows(rows);
      const pv = await api.post('/seller/inventory/bulk-preview', { rows });
      setPreview(pv.data);
      toast.success(`파싱 완료: ${pv.data.total}행 (정상 ${pv.data.valid_count}, 오류 ${pv.data.error_count})`);
    } catch (e) { toast.error(e?.response?.data?.detail || `파일 처리 실패: ${e.message}`); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    if (!preview) return;
    if (preview.valid_count === 0) { toast.error('적용할 정상 행이 없습니다.'); return; }
    if (!window.confirm(`정상 ${preview.valid_count}행을 적용하시겠습니까?`)) return;
    setBusy(true);
    try {
      const r = await api.post('/seller/inventory/bulk-apply', { rows: preview.rows });
      toast.success(`${r.data.applied_count}행 적용 완료 (스킵 ${r.data.skipped_count})`);
      onApplied?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || '적용 실패'); }
    finally { setBusy(false); }
  };

  const errorRows = (preview?.rows || []).filter((r) => r.status !== 'ok');
  const okRows = (preview?.rows || []).filter((r) => r.status === 'ok');

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-2 sm:p-4" data-testid="inv-bulk-modal">
      <div className="w-full sm:max-w-3xl bg-white dark:bg-[#141414] rounded-2xl p-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-lg font-black">재고 대량등록·수정</p>
            <p className="text-xs text-gray-500">xlsx/csv 파일을 업로드하면 미리보기 후 [적용]을 눌러야 실제 재고에 반영됩니다.</p>
          </div>
          <button onClick={onClose} className="p-1"><X size={16}/></button>
        </div>

        {!preview ? (
          <>
            <label className="block border-2 border-dashed border-black/10 dark:border-white/20 rounded-2xl p-8 text-center cursor-pointer hover:bg-black/5" data-testid="inv-bulk-drop">
              <FileSpreadsheet className="mx-auto text-gray-400 mb-2" size={32} />
              <p className="text-sm font-black">클릭 또는 파일 드래그</p>
              <p className="text-[11px] text-gray-500 mt-1">xlsx · csv · xls (최대 5000행)</p>
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; setFile(f); parseFile(f); }}
                data-testid="inv-bulk-file" />
            </label>
            <div className="mt-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 p-3 text-[11px]">
              <p className="font-black mb-1">📋 필요 컬럼: 상품ID · 옵션 · SKU · 조정수량 · 조정사유</p>
              <p className="text-gray-600 dark:text-gray-300">현재 필터의 [재고 다운로드]에서 받은 파일을 조정수량만 채워 그대로 업로드하면 안전합니다.</p>
            </div>
            {busy && <div className="mt-3 text-center text-xs text-gray-500"><Loader2 className="animate-spin inline mr-1" size={12}/> 파싱 중…</div>}
          </>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="rounded-xl bg-black/[0.03] dark:bg-white/[0.03] p-3 text-center">
                <p className="text-[10px] text-gray-500">전체</p><p className="font-black text-lg" data-testid="inv-bulk-total">{preview.total}</p>
              </div>
              <div className="rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 p-3 text-center">
                <p className="text-[10px] text-emerald-700 dark:text-emerald-300">정상</p><p className="font-black text-lg text-emerald-700 dark:text-emerald-300" data-testid="inv-bulk-valid">{preview.valid_count}</p>
              </div>
              <div className="rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 p-3 text-center">
                <p className="text-[10px] text-red-700 dark:text-red-300">오류</p><p className="font-black text-lg text-red-700 dark:text-red-300" data-testid="inv-bulk-errors">{preview.error_count}</p>
              </div>
            </div>

            {errorRows.length > 0 && (
              <div className="rounded-xl border border-red-200 dark:border-red-500/30 p-3 max-h-40 overflow-y-auto" data-testid="inv-bulk-error-list">
                <p className="text-[11px] font-black text-red-700 dark:text-red-300 mb-2 flex items-center gap-1"><AlertCircle size={11}/> 오류 {errorRows.length}행</p>
                {errorRows.map((r, i) => (
                  <div key={i} className="text-[10px] flex justify-between border-b border-red-100 dark:border-red-500/20 py-1">
                    <span>{r.input?.sku || r.input?.product_id || `행 ${r.row_index + 1}`}</span>
                    <span className="text-red-600 dark:text-red-400 font-bold">{(r.errors || []).join(' · ')}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="rounded-xl border border-black/10 dark:border-white/10 max-h-72 overflow-y-auto" data-testid="inv-bulk-preview-list">
              <table className="w-full text-[11px]">
                <thead className="bg-black/[0.03] dark:bg-white/5 sticky top-0">
                  <tr className="text-left">
                    <th className="px-2 py-1.5">상품</th>
                    <th className="px-2 py-1.5">옵션</th>
                    <th className="px-2 py-1.5">SKU</th>
                    <th className="px-2 py-1.5 text-right">현재</th>
                    <th className="px-2 py-1.5 text-right">변경</th>
                    <th className="px-2 py-1.5 text-right">변경 후</th>
                    <th className="px-2 py-1.5">상태</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} className="border-t border-black/5 dark:border-white/10">
                      <td className="px-2 py-1 truncate">{r.product_name || r.input?.product_id || '-'}</td>
                      <td className="px-2 py-1 text-gray-500">{r.variant_key === 'default' ? '기본' : (r.variant_key || r.input?.variant_key || '-')}</td>
                      <td className="px-2 py-1 font-mono text-gray-500">{r.sku || r.input?.sku || '-'}</td>
                      <td className="px-2 py-1 text-right">{r.before_stock ?? '-'}</td>
                      <td className={`px-2 py-1 text-right font-bold ${r.delta > 0 ? 'text-emerald-600' : r.delta < 0 ? 'text-red-600' : ''}`}>{r.delta > 0 ? '+' : ''}{r.delta ?? '-'}</td>
                      <td className="px-2 py-1 text-right font-black">{r.after_stock ?? '-'}</td>
                      <td className="px-2 py-1">
                        {r.status === 'ok' ? (
                          <span className="text-emerald-600 font-bold">정상</span>
                        ) : (
                          <span className="text-red-600 font-bold">{(r.errors || [])[0] || '오류'}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-2">
              <button onClick={() => { setPreview(null); setFile(null); setRawRows([]); }} className="flex-1 py-3 rounded-full border border-black/10 dark:border-white/10 text-xs font-black" data-testid="inv-bulk-reset">파일 다시 선택</button>
              <button onClick={apply} disabled={busy || preview.valid_count === 0} className="flex-1 py-3 rounded-full bg-[#FF3B30] text-white text-xs font-black disabled:opacity-40" data-testid="inv-bulk-apply">
                {busy ? '적용 중…' : `정상 ${preview.valid_count}행 적용`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
